import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import {
    selectCurrentEntityType,
    selectDeleteCurrentTenantId,
    setCurrentEntityType,
    setDialogOpen
} from 'common/components/deleteDialog/deleteDialogSlice';
import { deleteUserProfileGlobalRole } from 'common/services/api/UserProfileGlobalRoles/UserProfileGlobalRoles.thunk';
import { DeleteDialogContentProps, DrawerEntitiesNames } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

export function UserProfileGlobalRolesDeleteDialog({
    tenant,
    itemId,
    customDeleteTitle,
    editingRow,
}: Readonly<DeleteDialogContentProps<{
    id: string,
    name: string
}>>) {
    const { name, id } = editingRow ?? { };
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const currentEntityType = useAppSelector(selectCurrentEntityType);
    const currentTenantId = useAppSelector(selectDeleteCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);
    const { firstName, lastName } = userDetails ?? {};
    const params = useParams();

    const successDeleteHandler = (): void => {
        dispatch(setDialogOpen(false));
        dispatch(setCurrentEntityType());
        enqueueSnackbar('The Global Role has been removed from this User under this Tenant.', { variant: 'success' });
        updateUserProfileTenant({ tenantId: tenant?.id ?? '', userId: params?.userId ?? '' });
    };

    const userGlobalRolesDeleteCallback = useCallback(() => {
        if (!itemId) {
            return;
        }
        setProcessing(true);
        dispatch(deleteUserProfileGlobalRole({
            tenantId: tenant?.id ?? '',
            userId: userDetails?.userId ?? '',
            globalRoleId: id,
        }))
            .unwrap()
            .then(successDeleteHandler)
            .catch(() => {
                enqueueSnackbar(
                    'There was an error while deleting this Global Role.' +
                    DEFAULT_ERROR_MESSAGE,
                    { variant: 'error' }
                );
            })
            .finally(()=> {
                setProcessing(false);
            });
    }, [itemId]);

    if (currentEntityType !== DrawerEntitiesNames.UserGlobalRoles || currentTenantId !== tenant?.id) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={userGlobalRolesDeleteCallback} processing={processing ?? false} customDeleteTitle={customDeleteTitle}>
            <DeleteDialogCustomField fieldName="Global Role Name" fieldValue={name} />
            <DeleteDialogCustomField fieldName="User" fieldValue={`${firstName} ${lastName}`} />
            <DeleteDialogCustomField fieldName="Tenant Name" fieldValue={tenant?.name} />
        </DeleteDialog>
    );
}
